import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import HomeChat from "../Home/HomeChat";
import { HOME_CHAT_THEME } from "../Home/homeChatTheme";
import { useTheme } from "../../ThemeProvider/ThemeContext";

const AssistantContext = createContext(null);

// null when no app-level assistant is mounted (signed out, mobile).
export const useAssistant = () => useContext(AssistantContext);

/**
 * Owns the single EMAlpha Assistant conversation for the whole app. It sits
 * outside the routed pages, so the panel and its minimized dock survive
 * navigation; pages such as Home only render a launcher that asks through
 * this context.
 */
const AssistantProvider = ({ enabled, tabs = [], children }) => {
  const chatRef = useRef(null);
  const { isDark } = useTheme();
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}`;
  const lastRouteKeyRef = useRef(routeKey);

  // Dock the assistant when the page changes underneath it (e.g. a product
  // suggestion link) so the destination page is visible.
  useEffect(() => {
    if (lastRouteKeyRef.current === routeKey) return;
    lastRouteKeyRef.current = routeKey;
    chatRef.current?.minimize();
  }, [routeKey]);

  const value = useMemo(
    () => ({
      ask: (question, evidenceRefs) =>
        chatRef.current?.askQuestion(question, evidenceRefs) ?? false,
    }),
    [],
  );

  return (
    <AssistantContext.Provider value={enabled ? value : null}>
      {children}
      {enabled &&
        createPortal(
          <HomeChat
            ref={chatRef}
            hideLauncher
            origin="FXGPT"
            defaultCompanyIds={[""]}
            defaultDashboardName=""
            defaultCountries={[""]}
            expandedHeight="min(780px, calc(100vh - 144px))"
            expandedPresentation="overlay"
            expandedOverlayTop={112}
            expandedOverlayWidth="min(1240px, calc(100vw - 120px))"
            themeStyles={isDark ? undefined : HOME_CHAT_THEME}
            availableTabs={tabs}
          />,
          document.body,
        )}
    </AssistantContext.Provider>
  );
};

export default AssistantProvider;
